import { PaymentTypesEnum, ServiceTypesEnum } from "@propertelligent/client-api";

import { parsePhoneNumberFromString } from "libphonenumber-js";

class UtilHelper {
    CurrencyFormat = (amount, isCurrencySymbol = true) => {
        const formattedAmount = amount.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

        // Add space after the dollar sign
        return formattedAmount.replace('$', isCurrencySymbol ? '$ ' : '');
    }

    formatCurrency(value, isCurrencySymbol = true, currencySymbol = "$") {
        if (isNaN(value)) return `${currencySymbol}0.00`;

        const suffixes = ["", "K", "M", "B", "T", "Q", "Qu", "Sx", "Sp", "Oc", "N"];
        let num = Math.abs(value);
        let i = 0;

        while (num >= 1000 && i < suffixes.length - 1) {
            num /= 1000;
            i++;
        }

        const formattedNumber = value < 1000
            ? num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : num % 1 === 0
                ? num.toLocaleString("en-US", { maximumFractionDigits: 0 })  // No decimals for whole numbers
                : num.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }); // 1 decimal place for large values

        return value < 0 
            ? `(${isCurrencySymbol ? currencySymbol : ""}${formattedNumber}${suffixes[i]})` 
            : isCurrencySymbol ? `${currencySymbol}${formattedNumber}${suffixes[i]}` : `${formattedNumber}${suffixes[i]}`;
    }


      formatStringNumber = (num) => {

        if (typeof num === "string") {
          num = parseFloat(num.replace(/,/g, ""));
        }
      
        const suffixes = ["", "K", "M", "B", "T", "Q", "Qu", "Sx", "Sp", "Oc", "N"];
        let tier = Math.floor(Math.log10(num) / 3);
      
        if (tier === 0) return this.CurrencyFormat(num);
      
        tier = Math.min(tier, suffixes.length - 1);
        return `$ ${(num / Math.pow(10, tier * 3)).toFixed(2)}${suffixes[tier]}`;
      };



    formatCurrencyForNagative(value, currencySymbol = "$") {
        if (isNaN(value)) return `${currencySymbol}0.00`;

        const suffixes = ["", "K", "M", "B", "T", "Q", "Qu", "Sx", "Sp", "Oc", "N"];
        let num = Math.abs(value);
        let i = 0;

        while (num >= 1000 && i < suffixes.length - 1) {
            num /= 1000;
            i++;
        }

        const formattedNumber = value < 1000
            ? num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : num % 1 === 0
                ? num.toLocaleString("en-US", { maximumFractionDigits: 0 })  // No decimals for whole numbers
                : num.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }); // 1 decimal place for large values

        // Correct format for negative values
        return value < 0 ? `-${currencySymbol}${formattedNumber}${suffixes[i]}` : `${currencySymbol}${formattedNumber}${suffixes[i]}`;
    }


    formatPhoneForDisplay = (input?: string) =>
        !input ? "" : parsePhoneNumberFromString(input, "US")?.formatNational() ?? input;

    /** US/intl display including country calling code, e.g. "+1 732 321 5588". */
    formatPhoneWithCountryCode = (input?: string) => {
        if (!input) return "";
        const parsed = parsePhoneNumberFromString(input, "US");
        return parsed?.formatInternational() ?? input;
    };

    /** Digits for PhoneInput / dirty compare: strip non-digits, drop leading US/CA "1" for equality. */
    normalizePhoneDigits = (input?: string | null) => {
        const digits = (input ?? "").replace(/\D/g, "");
        return digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
    };

    /** Typed-amount guard matching CurrencyInput's 99999999.99 cap: up to 8 integer digits, 2 decimals. */
    isValidAmountInput = (value: string) => /^\d{0,8}(\.\d{0,2})?$/.test(value);

    /** Prefixed digits for react-phone-input-2 so the +1 country dial shows by default. */
    toPhoneInputValue = (input?: string | null) => {
        const digits = (input ?? "").replace(/\D/g, "");
        if (!digits) return "";
        if (digits.length === 10) return `1${digits}`;
        return digits;
    };

    /** E.164 text for copying: stored numbers already carry "+", so only prefix when missing. */
    toE164Text = (input?: string | null) => {
        const value = (input ?? "").trim();
        if (!value) return "";
        return value.startsWith("+") ? value : `+${value}`;
    };

    formatCustomPhoneNumber = (input: string) => {

        if (!input) return "";

        const rawNumber = input.startsWith("+") ? input : `+${input}`;

        const phoneNumber = parsePhoneNumberFromString(rawNumber);
        if (!phoneNumber) return "Invalid number";

        const countryCode = phoneNumber.countryCallingCode;

        const nationalNumber = phoneNumber.nationalNumber;

        if (nationalNumber.length < 9) return "Invalid number";

        const part1 = nationalNumber.slice(0, 2);
        const part2 = nationalNumber.slice(2, 5);
        const part3 = nationalNumber.slice(5);

        return `+${countryCode}-${part1}-${part2}-${part3}`;
    };

    ExpenseTypeColors(expenseType: number): string {
        const expenseTypeColors: Record<number, string> = {
            [PaymentTypesEnum.Water]: "#3498db",           // Light Blue
            [PaymentTypesEnum.Electric]: "#f1c40f",       // Yellow
            [PaymentTypesEnum.Gas]: "#e67e22",            // Orange
            [PaymentTypesEnum.Sewer]: "#f18288",
            [PaymentTypesEnum.Phone]: "#9b59b6",          // Purple
            [PaymentTypesEnum.Internet]: "#34495e",       // Dark Gray-Blue
            [PaymentTypesEnum.Cable]: "#e74c3c",          // Red
            [PaymentTypesEnum.LandTax]: "#1eb71e",        // Green
            [PaymentTypesEnum.CountyTax]: "#8e44ad",      // Dark Purple
            // [PaymentTypesEnum.TownshipMunicipalTax]: "#00C769",
            [PaymentTypesEnum.StateTax]: "#2980b9",       // Dark Blue
            [PaymentTypesEnum.FederalTax]: "#FFC400",
            // [PaymentTypesEnum.Payroll]: "#27ae60",        // Green
            [PaymentTypesEnum.LiabilityInsurance]: "#7f8c8d",  // Gray
            [PaymentTypesEnum.WorkersCompensation]: "#f39c12", // Gold
            [PaymentTypesEnum.RenterInsurance]: "#16a085",    // Sea Green
            [PaymentTypesEnum.PropertyInsurance]: "#2c3e50",  // Navy Blue
            [PaymentTypesEnum.BankFee]: "#bdc3c7",        // Silver
            [PaymentTypesEnum.Mortgage]: "#ff5733",       // Bright Orange
            // [PaymentTypesEnum.LegalFee]: "#6c3483",       // Deep Purple
            [PaymentTypesEnum.ViolationCharge]: "#cd6155", // Salmon
            [PaymentTypesEnum.HoaFee]: "#f7dc6f",         // Light Yellow
            [PaymentTypesEnum.SecurityDeposit]: "#76d7c4", // Aqua
            [PaymentTypesEnum.Rent]: "#52be80",           // Light Green
            [PaymentTypesEnum.LateFee]: "#f5b041",        // Light Orange
            // [PaymentTypesEnum.Invoice]: "#a569bd",        // Light Purple
            [PaymentTypesEnum.Labor]: "#5dade2",          // Light Blue
            [PaymentTypesEnum.ServiceRequest]: "#3674b3", // Lime Green
            // [PaymentTypesEnum.SecurityDepositReturn]: "#eb984e", // Copper
            [PaymentTypesEnum.Refund]: "#dc7633",         // Burnt Orange
            // [PaymentTypesEnum.OtherSettlement]: "#af7ac5", // Lilac
            [PaymentTypesEnum.Commission]: "#5b2c6f",     // Royal Purple
            [PaymentTypesEnum.Other]: "#34495e",          // Dark Gray-Blue
            // [PaymentTypesEnum.HardwareStore]: "#117a65",  // Dark Teal
            // [PaymentTypesEnum.ApplianceStore]: "#a04000", // Dark Amber
            // [PaymentTypesEnum.FurnitureStore]: "#6e2c00", // Dark Brown
            // [PaymentTypesEnum.OtherStore]: "#1f618d",     // Navy Blue
            [PaymentTypesEnum.ElectricAndGas]: "#FF1F8B",
            // [PaymentTypesEnum.PlatformSubscription]: "#2e86c1", // Sky Blue
        };

        return expenseTypeColors[expenseType as PaymentTypesEnum] || "#7f8c8d"; // Default Gray
    }

    ServiceTypeColors(Type: number): string {
        const serviceTypeColors: Record<number, string> = {
            [ServiceTypesEnum.Plumbing]: "#FFC400",
            [ServiceTypesEnum.Appliance]: "#16a085",
            [ServiceTypesEnum.Electrical]: "#8e44ad",
            [ServiceTypesEnum.Exterior]: "#FF1F8B",
            [ServiceTypesEnum.Handyman]: "#cd6155",
            [ServiceTypesEnum.Interior]: "#3F83F8",
            [ServiceTypesEnum.Other]: "#FDE8E8",
            // [ServiceTypesEnum.Flooring]: "#00C769"
        }

        return serviceTypeColors[Type as ServiceTypesEnum] || "#7f8c8d"; // Default Gray
    }


    formatNegativeValue = (value) => {
        if (typeof value === 'number') {

            if (isNaN(value) || value === null || value === undefined) {
                return "0.00";
            }

            const formattedValue = value.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });

            if (value < 0) {
                return `(${formattedValue.replace('-', '')})`;
            }
            return formattedValue;
        }
        return value;
    };


    // This will remove the commas and parse the value to a number
    parseCurrencyToNumber = (value: string | number | undefined | null): number => {
        if (!value) return 0;
        if (typeof value === 'number') return value;
        const parsed = parseFloat(value.toString().replace(/,/g, ""));
        return isNaN(parsed) ? 0 : parsed;
    };


    getPaymentTypeName(paymentTypeId: number): string | undefined {
        const entry = Object.entries(PaymentTypesEnum).find(
            ([key, value]) => value === paymentTypeId
        );
        return entry ? entry[0] : undefined;
    }

    OnKeyDownForNumbers(e) {
        if (!/[0-9]/.test(e.key) && e.key !== "Backspace" && e.key !== "Tab" && e.key !== "ArrowLeft" && e.key !== "ArrowRight") {
            e.preventDefault();
        }
    }


    
 getInitials = (name: string): string => {
  const nameParts = name?.trim().split(" ");
  if (nameParts?.length === 1) return nameParts[0]?.charAt(0)?.toUpperCase();
  return (
    nameParts[0]?.charAt(0)?.toUpperCase() + nameParts[nameParts.length - 1]?.charAt(0).toUpperCase()
  );
};

}

export default new UtilHelper();